import React, { useContext, useState } from 'react'
import PropTypes from 'prop-types'

import {
  StyledGamesCarouselWrapper,
  StyledDesktopWrapper,
  StyledInnerMobileWrapper,
  StyledOuterMobileWrapper,
} from './styles'

import { ResponsiveContainer } from '../../atoms/responsiveContainer'
import { CarouselHeader } from '../../atoms/carouselHeader'
import { CarouselWrapper } from '../../atoms/carouselWrapper'
import { ContentUnWrapper } from '../../atoms/contentWrapper'
import { ClickableGameTile } from '../../atoms/clickableGameTile'
import { GameTileText } from '../../atoms/gameTileText'
import { If } from '../../atoms/if'
import { themeSizes } from '../../../styles/theme'
import { game_proptype } from '../../../propTypes'
import useSlugs from '../../../hooks/useSlugs'
import { GamesCarouselFallback } from './fallback'
import { AuthContext } from '../../../context/authProvider'

const GamesCarousel = (props) => {
  const { content, forceLogin, screenName, triggerGAEvent } = props
  const { isLoggedIn } = useContext(AuthContext)
  const [
    baseProvidersSlug,
    casinoSlug,
    liveCasinoSlug,
    loginSlug,
    bingoSlug,
    crashGamesSlug,
  ] = useSlugs([
    'providers',
    'casino',
    'live-casino',
    'login',
    'bingo',
    'crash-games',
  ])
  const [index, setIndex] = useState(0)

  const parseHomeCarouselData = () => {
    const category = content?.list_of_games?.[0] ?? {}
    return {
      ...category,
      ...content,
      ...content.list_of_games[0],
      maxItemsVisible: content.max_items_visible,
    }
  }

  const parseCasinoCarouselData = () => {
    const category = content?.game_category?.[0] ?? {}

    let categorySlug = liveCasinoSlug
    if (category.type === 'casino') categorySlug = casinoSlug
    else if (category.type === 'bingo') categorySlug = bingoSlug
    else if (category.type === 'crashgames') categorySlug = crashGamesSlug

    return {
      ...category,
      ...content,
      maxItemsVisible: content.max_items_visible,
      hideTile: !!category.hide_tile,
      header_title: content.title,
      link: category.slug ? `/${categorySlug}/${category.slug}` : null,
      games: category.carousel_games,
    }
  }

  const parseContent = () => {
    if (content?.list_of_games?.length > 0) {
      return parseHomeCarouselData()
    } else {
      return parseCasinoCarouselData()
    }
  }
  const parsedContent = parseContent()
  if (!parsedContent.games) return <GamesCarouselFallback />

  const {
    header_title,
    link,
    tile_display,
    background_mode,
    category_title_tag,
    logged_out_description,
    visibility_setting,
    maxItemsVisible = 6,
    hideTile = false,
    hideGameTags = false,
  } = parsedContent

  const games = parsedContent.games.filter((game) => !!game && !game?.mock)
  const isWide = tile_display == 'show_wide'
  const theme = background_mode

  const setSize = () => {
    if (!isWide && content.size === 'large') {
      return 'large'
    }

    if (isWide && content.size === 'large') {
      return 'small'
    }

    return content.size
  }

  const size = setSize()

  const { length } = games
  const maxIndex = Math.ceil(length / maxItemsVisible)

  const onNext = () => {
    const newIndex = index + 1
    if (newIndex < maxIndex) setIndex(newIndex)
    else setIndex(0)
  }

  const onPrevious = () => {
    const newIndex = index - 1
    if (newIndex >= 0) setIndex(newIndex)
    else setIndex(maxIndex - 1)
  }

  const nonAllowedVisibility = isLoggedIn ? 'logged_out' : 'logged_in'
  const visibilitySettingAligned = visibility_setting !== nonAllowedVisibility
  const allNecessaryPropsMissing =
    background_mode === '' && tile_display === '' && link === ''

  if (length === 0 || allNecessaryPropsMissing || !visibilitySettingAligned)
    return <></>

  // eslint-disable-next-line react/display-name
  const createMobileVersionFunction = (key) => (
    <StyledOuterMobileWrapper key={key}>
      <StyledInnerMobileWrapper length={length} size={size}>
        {games.map((slide, i) => (
          <div key={`game-${header_title}-${i}`}>
            <ClickableGameTile
              game={slide}
              key={`mobile-game-tile-${i}`}
              isWide={isWide}
              casinoSlug={casinoSlug}
              loginSlug={loginSlug}
              forceLogin={forceLogin}
              hideGameTags={hideGameTags}
            />
            <If
              condition={!hideTile}
              render={() => (
                <GameTileText
                  key={`mobile-game-title-${i}`}
                  title={slide.title}
                  providerName={slide.providerName}
                  gameSlug={slide.providerSlug || slide.providerCode}
                  baseProvidersSlug={baseProvidersSlug}
                />
              )}
            />
          </div>
        ))}
      </StyledInnerMobileWrapper>
    </StyledOuterMobileWrapper>
  )

  return (
    <StyledGamesCarouselWrapper theme={theme}>
      <CarouselHeader
        title={header_title}
        href={link}
        description={logged_out_description}
        titleTag={category_title_tag}
        hideDescriptionOnLoggedIn
        screenName={screenName}
        triggerGAEvent={triggerGAEvent}
      />
      <ResponsiveContainer
        sizeNumber={themeSizes.desktop}
        render={() => (
          <ContentUnWrapper addPaddingToChild>
            {createMobileVersionFunction('MobileGamesCarousel')}
          </ContentUnWrapper>
        )}
        renderElse={() => (
          <StyledDesktopWrapper key="DesktopGamesCarousel">
            <CarouselWrapper
              length={length}
              index={index}
              onNext={onNext}
              onPrevious={onPrevious}
              maxItemsVisible={maxItemsVisible}
            >
              {games.map((slide, i) => (
                <ClickableGameTile
                  game={slide}
                  key={`desktop-game-tile-${i}`}
                  isWide={isWide}
                  casinoSlug={casinoSlug}
                  loginSlug={loginSlug}
                  forceLogin={forceLogin}
                  hideGameTags={hideGameTags}
                  triggerGAEvent={triggerGAEvent}
                  screenName={screenName}
                  position={i}
                  category={header_title}
                />
              ))}
            </CarouselWrapper>
            <If
              condition={!hideTile}
              render={() => (
                <CarouselWrapper
                  length={length}
                  index={index}
                  maxItemsVisible={maxItemsVisible}
                  hideChildStyle={true}
                  hideHoverBorder
                >
                  {games.map((slide, i) => (
                    <GameTileText
                      key={`desktop-game-title-${i}`}
                      title={slide.title}
                      providerName={slide.providerName}
                      gameSlug={slide.providerSlug || slide.providerCode}
                      baseProvidersSlug={baseProvidersSlug}
                    />
                  ))}
                </CarouselWrapper>
              )}
            />
          </StyledDesktopWrapper>
        )}
        renderFallback={() => (
          <ContentUnWrapper>
            {createMobileVersionFunction('FallbackGamesCarousel')}
          </ContentUnWrapper>
        )}
      />
    </StyledGamesCarouselWrapper>
  )
}

const homeGamePropType = PropTypes.shape({
  logged_out_description: PropTypes.string,
  header_title: PropTypes.string,
  max_items_visible: PropTypes.number,
  list_of_games: PropTypes.arrayOf(
    PropTypes.shape({
      link: PropTypes.string,
      tile_display: PropTypes.bool,
      background_mode: PropTypes.bool,
      games: PropTypes.arrayOf(game_proptype),
    })
  ),
})

const casinoGamePropType = PropTypes.shape({
  logged_out_description: PropTypes.string,
  category_title_tag: PropTypes.string,
  size: PropTypes.string,
  visibility_setting: PropTypes.string,
  title: PropTypes.string,
  max_items_visible: PropTypes.number,
  game_category: PropTypes.arrayOf(
    PropTypes.shape({
      slug: PropTypes.string,
      carousel_games: PropTypes.arrayOf(game_proptype),
    })
  ),
})

GamesCarousel.propTypes = {
  content: PropTypes.oneOfType([homeGamePropType, casinoGamePropType]),
  forceLogin: PropTypes.bool,
  paths: PropTypes.object,
  screenName: PropTypes.string,
  triggerGAEvent: PropTypes.bool,
}

export { GamesCarousel }
